import React from "react";
import { Helmet } from "react-helmet-async";
import Grid, { type Actions, type Columns } from "../../component/Grid";
import { useCrumbs } from "../../component/breadcrumbs";
import { useNavigate, useSearchParams } from "react-router-dom";
import useLocale from "../../util/i18n";
import { dismissCreditMemo, getCreditMemo, getCreditMemoStatuses, saveCreditMemo, type CreditMemo, type CreditMemoItem } from "../../api/erp";
import { getConfig } from "../../api/core";
import { type OptionType } from "../../component/select";
import { format } from "date-fns";
import { MDBBtn, MDBInput, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalFooter } from "mdb-react-ui-kit";
import ExcelJS from "exceljs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import Toast from "../../component/toast";
import BigNumber from "bignumber.js";

export default function CreditMemos() {
    let { 0: editable, 1: setEditable } = React.useState(false);
    let { 0: query, 1: setQuery } = useSearchParams();
    let { 0: statuses, 1: setStatuses } = React.useState<Array<OptionType>>([]);
    let { 0: setCrumbs } = useCrumbs();
    let { 0: __, 1: lang, 3: locale } = useLocale();
    let { 0: loading, 1: setLoading } = React.useState(false);
    let navigate = useNavigate();
    const formatters = React.useRef<Record<string, Intl.NumberFormat>>({});
    let columns = React.useMemo<Columns>(() => ({
        credit_id: {
            label: 'ID',
            filterable: 'filter[credit_id]'
        },
        subtotal: {
            label: __('Subtotal'),
            className: 'w-1 text-end text-nowrap',
            format: (value, item) => (formatters.current[item.currency] ??= new Intl.NumberFormat(lang, { style: 'currency', currency: item.currency })).format(value)
        },
        tax: {
            label: __('Tax'),
            className: 'w-1 text-end text-nowrap',
            format: (value, item) => (formatters.current[item.currency] ??= new Intl.NumberFormat(lang, { style: 'currency', currency: item.currency })).format(value)
        },
        total: {
            label: __('Grand Total'),
            className: 'w-1 text-end text-nowrap',
            format: (value, item) => (formatters.current[item.currency] ??= new Intl.NumberFormat(lang, { style: 'currency', currency: item.currency })).format(value)
        },
        created_at: {
            label: __('Created at'),
            format: value => parseInt(value) ? format(value * 1000, 'PPPP', { locale }) : '-'
        },
        status: {
            label: __('Status'),
            className: 'w-1 text-center text-nowrap',
            filterable: 'filter[status]',
            type: 'select',
            options: statuses
        }
    }), [__, lang, statuses]);
    let actions = React.useMemo<Actions>(() => ([
        {
            label: __(editable ? 'Edit' : 'View'),
            onClick: (item: CreditMemo) => {
                window.sessionStorage.setItem('CM-' + item.id, JSON.stringify(item));
                navigate('/credit-memo/' + (editable ? 'edit' : 'view') + '?id=' + item.id);
            }
        },
        {
            label: __('Excluded in Statistics'),
            condition: item => item.dismiss != 1,
            onClick: (item, ref) => {
                dismissCreditMemo({ id: item.id, dismiss: 1 }).then(() => {
                    ref.redraw({ ...item, dismiss: 1 });
                });
            }
        },
        {
            label: __('Included in Statistics'),
            condition: item => item.dismiss == 1,
            onClick: (item, ref) => {
                dismissCreditMemo({ id: item.id, dismiss: 0 }).then(() => {
                    ref.redraw({ ...item, dismiss: 0 });
                });
            }
        }
    ]), [__, editable]);
    const doLoad = React.useCallback((params: Record<string, any>) => {
        if (!params.sort) {
            params.sort = 'created_at DESC';
        }
        return getCreditMemo(params);
    }, []);
    const upload = React.useRef<(file: File) => Promise<boolean>>(null);
    let onDropFile = React.useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        let files = e.dataTransfer.files;
        if (files.length === 0) {
            return;
        }
        setLoading(true);
        for (let i = 0; i < files.length; i++) {
            const file = files.item(i);
            file && (await upload.current?.(file));
        }
        setLoading(false);
        document.location.reload();
    }, [setLoading]);
    React.useEffect(() => {
        setCrumbs(['Credit Memo']);
        if (window.sessionStorage.getItem('erp-editable')) {
            setEditable(window.sessionStorage.getItem('erp-editable') === '1');
        } else {
            getConfig('erp/service').then(response => {
                let result = !Boolean(response['erp/service']) ? '1' : '0';
                window.sessionStorage.setItem('erp-editable', result);
                setEditable(result === '1');
            });
        }
        let statuses = window.sessionStorage.getItem('CM-status');
        if (statuses) {
            setStatuses(JSON.parse(statuses));
        } else {
            getCreditMemoStatuses().then(response => {
                let result = response.map(item => ({ value: item.status, label: item.status }));
                window.sessionStorage.setItem('CM-status', JSON.stringify(result));
                setStatuses(result);
            });
        }
    }, [setEditable, setStatuses]);
    return (
        <>
            <Helmet>
                <title>{__('Credit Memo')}</title>
            </Helmet>
            <Grid newUrl={editable ? '/credit-memo/edit' : undefined} onDrop={editable ? onDropFile : undefined} columns={columns} actions={actions} query={query} setQuery={setQuery} doLoad={doLoad} />
            {editable ? (
                <>
                    <Import ref={upload} />
                    {loading ? (
                        <div className="loading-modal">
                            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                        </div>
                    ) : null}
                </>
            ) : null}
        </>
    );
}

const DATES: Record<number, 'created_at'> = {
    5: 'created_at'
};

const Import = React.forwardRef<(file: File) => Promise<boolean>>(function (_, ref) {
    let { 0: show, 1: setShow } = React.useState(false);
    let { 0: loading, 1: setLoading } = React.useState(false);
    let { 0: __ } = useLocale();
    let template = React.useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet();
        sheet.getCell(1, 1).value = __('Credit Memo ID');
        sheet.getCell(2, 1).value = __('Currency');
        sheet.getCell(2, 2).value = 'USD';
        sheet.getCell(3, 1).value = __('Subtotal');
        sheet.getCell(3, 2).value = '[AUTO]';
        sheet.getCell(4, 1).value = __('Tax');
        sheet.getCell(4, 2).value = '[AUTO]';
        sheet.getCell(5, 1).value = __('Total');
        sheet.getCell(5, 2).value = '[AUTO]';
        sheet.getCell(6, 1).value = __('Created at');
        sheet.getCell(7, 1).value = __('Status');
        sheet.getCell(8, 1).value = 'SKU';
        sheet.getCell(8, 2).value = __('Qty');
        sheet.getCell(8, 3).value = __('Unit Price');
        sheet.getCell(8, 4).value = __('Tax');
        sheet.getCell(8, 5).value = __('Reason');
        sheet.getCell(8, 6).value = __('Comment');
        sheet.getCell(1, 1).style.font = sheet.getCell(2, 1).style.font = sheet.getCell(3, 1).style.font = sheet.getCell(4, 1).style.font =
            sheet.getCell(5, 1).style.font = sheet.getCell(6, 1).style.font = sheet.getCell(7, 1).style.font
        sheet.getCell(8, 1).style.font = sheet.getCell(8, 2).style.font = sheet.getCell(8, 3).style.font = sheet.getCell(8, 4).style.font = sheet.getCell(8, 5).style.font = sheet.getCell(8, 6).style.font = { bold: true };
        workbook.xlsx.writeBuffer().then(buffer => {
            let a = document.createElement('a');
            a.download = 'template-CM.xlsx';
            a.href = URL.createObjectURL(new Blob([buffer]));
            document.body.append(a);
            a.click();
            URL.revokeObjectURL(a.href);
            a.remove();
        });
    }, [__]);
    let doSubmit = React.useCallback(async (file: File) => {
        try {
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(await file.arrayBuffer());
            let params: Array<Partial<CreditMemo> & { item: Array<CreditMemoItem> }> = [];
            workbook.eachSheet(sheet => {
                let order: Partial<Omit<CreditMemo, 'subtotal' | 'tax' | 'total'>> & { subtotal: BigNumber | number, tax: BigNumber | number, total: BigNumber | number, item: Array<CreditMemoItem> } = {
                    credit_id: sheet.getCell(1, 2).text,
                    currency: sheet.getCell(2, 2).text || 'USD',
                    subtotal: sheet.getCell(3, 2).text === '[AUTO]' ? new BigNumber(0) : parseFloat(sheet.getCell(2, 2).text),
                    tax: sheet.getCell(4, 2).text === '[AUTO]' ? new BigNumber(0) : parseFloat(sheet.getCell(2, 2).text),
                    total: sheet.getCell(5, 2).text === '[AUTO]' ? new BigNumber(0) : parseFloat(sheet.getCell(2, 2).text),
                    status: sheet.getCell(9, 2).text,
                    created_at: 0,
                    updated_at: Math.round(Date.now() / 1000),
                    item: []
                };
                if (!order.credit_id) {
                    return;
                }
                for (let r in DATES) {
                    let value = sheet.getCell(parseInt(r), 2).value;
                    if (value && value instanceof Date) {
                        order[DATES[r] as 'created_at'] = Math.round(value.getTime() / 1000)
                    }
                }
                for (let r = 9; ; r++) {
                    let row = sheet.getRow(r);
                    let item: CreditMemoItem = {
                        id: '',
                        credit_id: order.credit_id,
                        sku: row.getCell(1).text.trim(),
                        qty: parseFloat(row.getCell(2).text) || 0,
                        credit_price: parseFloat(row.getCell(3).text) || 0,
                        tax: parseFloat(row.getCell(4).text) || 0,
                        rowtotal: 0,
                        comments: '',
                        reason: ''
                    }
                    let c5 = row.getCell(5).text, c6 = row.getCell(6).text, c7 = row.getCell(7).text;
                    if (c6 || Number.isNaN(parseFloat(c5))) {
                        item.rowtotal = parseFloat(c5);
                        item.reason = c6 || '';
                        item.comments = c7 || '';
                    } else {
                        item.rowtotal = item.credit_price || 0;
                        item.reason = c5 || '';
                        item.comments = c6 || '';
                    }
                    if (!item.sku) {
                        break;
                    }
                    if (item.qty > 0) {
                        if (order.subtotal instanceof BigNumber) {
                            order.subtotal = order.subtotal.plus(item.credit_price);
                        }
                        if (order.tax instanceof BigNumber) {
                            order.tax = order.tax.plus(item.tax);
                        }
                        if (order.total instanceof BigNumber) {
                            order.total = order.total.plus(item.rowtotal).plus(item.tax);
                        }
                        order.item.push(item);
                    }
                }
                if (order.subtotal instanceof BigNumber) {
                    order.subtotal = order.subtotal.toNumber();
                }
                if (order.tax instanceof BigNumber) {
                    order.tax = order.tax.toNumber();
                }
                if (order.total instanceof BigNumber) {
                    order.total = order.total.toNumber();
                }
                params.push(order as Partial<CreditMemo> & { item: Array<CreditMemoItem> });
            });
            await saveCreditMemo(params);
            return true;
        } catch {
            return false;
        }
    }, [])
    let onSubmit = React.useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.stopPropagation();
        e.preventDefault();
        let file = (new FormData(e.target as HTMLFormElement)).get('file') as File | null;
        if (file) {
            setLoading(true);
            if (await doSubmit(file)) {
                setShow(false);
                Toast.show(__('Data has been saved.')).then(() => {
                    window.location.reload();
                });
            }
            setLoading(false);
        }
    }, [setLoading, setShow]);
    React.useEffect(() => {
        let l = setShow.bind(null, true);
        document.addEventListener('import', l);
        return () => {
            document.removeEventListener('import', l);
        };
    }, [setShow]);
    React.useImperativeHandle(ref, () => doSubmit, [doSubmit]);
    return (
        <>
            <div data-importable="1" />
            <MDBModal open={show} onClose={setShow.bind(null, false)} tabIndex='-1'>
                <MDBModalDialog centered>
                    <MDBModalContent tag="form" onSubmit={onSubmit}>
                        <MDBModalBody>
                            {loading ? (
                                <div className="d-flex justify-content-center">
                                    <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                                </div>
                            ) : (
                                <MDBInput type="file" name="file" label={__('File')} className="active" required accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />
                            )}
                        </MDBModalBody>
                        <MDBModalFooter>
                            <MDBBtn type="button" color="info" onClick={template}>{__('Template')}</MDBBtn>
                            <MDBBtn disabled={loading} type="button" color="secondary" onClick={setShow.bind(null, false)}>{__('Cancel')}</MDBBtn>
                            <MDBBtn disabled={loading} type="submit">{__('Import')}</MDBBtn>
                        </MDBModalFooter>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
        </>
    );
});
